import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';

import Card from '@/components/Card';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import Avatar from '@/components/Avatar';
import {
  WorkersV2API,
  Worker,
  WorkersListParams,
  Pagination,
} from '@/services/workersV2API';

const ONBOARDING_STATUSES = [
  { value: '', label: 'All' },
  { value: 'onboarding_in_progress', label: 'In Progress' },
  { value: 'pending_ssn', label: 'Pending SSN' },
  { value: 'pending_payment_method', label: 'Pending Payment' },
  { value: 'pending_tax_withholding', label: 'Pending Tax' },
  { value: 'pending_workplace', label: 'Pending Workplace' },
  { value: 'onboarding_completed', label: 'Completed' },
];

const I9_STATUSES = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'needs_attention', label: 'Needs Attention' },
  { value: 'tentative_nc', label: 'Tentative NC' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_e-verify', label: 'Pending E-Verify' },
  { value: 'pending_section2', label: 'Pending Section 2' },
  { value: 'e-verify_in_progress', label: 'E-Verify In Progress' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'closed', label: 'Closed' },
  { value: 'n/a', label: 'N/A' },
];

const TAX_TYPES = [
  { value: '', label: 'All' },
  { value: 'w2', label: 'W2' },
  { value: '1099', label: '1099' },
];

const ACTIVE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Enabled' },
  { value: 'false', label: 'Disabled' },
];

const TABLE_HEADERS = [
  'ID',
  'Name',
  'Email',
  'Tax Type',
  'Onboarding',
  'I-9 Status',
  'Status',
];

const statusColors: Record<string, 'theme' | 'danger' | 'warning' | 'neutral'> =
  {
    // Onboarding statuses
    onboarding_completed: 'theme',
    onboarding_in_progress: 'warning',
    pending_ssn: 'warning',
    pending_payment_method: 'warning',
    pending_tax_withholding: 'warning',
    pending_workplace: 'warning',

    // I9 statuses
    approved: 'theme',
    pending: 'warning',
    rejected: 'danger',
    needs_attention: 'warning',
    tentative_nc: 'danger',
    in_progress: 'warning',
    cancelled: 'neutral',
    'pending_e-verify': 'warning',
    pending_section2: 'warning',
    'e-verify_in_progress': 'warning',
    prospect: 'neutral',
    closed: 'neutral',
    'n/a': 'neutral',
  };

const formatStatus = (status: string | null | undefined) => {
  if (!status) return 'N/A';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const WorkersV2 = () => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [onboardingStatus, setOnboardingStatus] = useState('');
  const [i9Status, setI9Status] = useState('');
  const [taxType, setTaxType] = useState('');
  const [active, setActive] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchWorkers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: WorkersListParams = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: 'created_at',
        sort_direction: 'desc',
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (onboardingStatus) params.onboarding_status = onboardingStatus;
      if (i9Status) params.i9_status = i9Status;
      if (taxType) params.tax_type = taxType as 'w2' | '1099';
      if (active) params.active = active === 'true';

      const response = await WorkersV2API.getWorkers(params);

      if (response.success) {
        setWorkers(response.data);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch workers');
      }
    } catch (err) {
      console.error('Error fetching workers:', err);
      setError('Failed to fetch workers');
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    onboardingStatus,
    i9Status,
    taxType,
    active,
  ]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleNumberItemsChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setCurrentPage(1);
    setItemsPerPage(parseInt(event.target.value));
  };

  const handleWorkerClick = (workerId: number) => {
    navigate(`/workers_v2/${workerId}`);
  };

  const clearFilters = () => {
    setSearch('');
    setOnboardingStatus('');
    setI9Status('');
    setTaxType('');
    setActive('');
    setCurrentPage(1);
  };

  const hasActiveFilters =
    search || onboardingStatus || i9Status || taxType || active;

  return (
    <Page size="full" title="Workers V2">
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Stack gap={16} style={{ flexWrap: 'wrap' }}>
              <TextField
                label="Search"
                placeholder="Search by name, email, phone..."
                size="small"
                sx={{ minWidth: 250, flex: 1 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Onboarding Status</InputLabel>
                <Select
                  label="Onboarding Status"
                  value={onboardingStatus}
                  onChange={(e: SelectChangeEvent) => {
                    setOnboardingStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {ONBOARDING_STATUSES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>I-9 Status</InputLabel>
                <Select
                  label="I-9 Status"
                  value={i9Status}
                  onChange={(e: SelectChangeEvent) => {
                    setI9Status(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {I9_STATUSES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Tax Type</InputLabel>
                <Select
                  label="Tax Type"
                  value={taxType}
                  onChange={(e: SelectChangeEvent) => {
                    setTaxType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {TAX_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={active}
                  onChange={(e: SelectChangeEvent) => {
                    setActive(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {ACTIVE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {hasActiveFilters && (
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <Small color="lighter">Active filters:</Small>
                {search && (
                  <Chip
                    label={`Search: ${search}`}
                    size="small"
                    onDelete={() => setSearch('')}
                  />
                )}
                {onboardingStatus && (
                  <Chip
                    label={`Onboarding: ${formatStatus(onboardingStatus)}`}
                    size="small"
                    onDelete={() => setOnboardingStatus('')}
                  />
                )}
                {i9Status && (
                  <Chip
                    label={`I-9: ${formatStatus(i9Status)}`}
                    size="small"
                    onDelete={() => setI9Status('')}
                  />
                )}
                {taxType && (
                  <Chip
                    label={`Tax: ${taxType.toUpperCase()}`}
                    size="small"
                    onDelete={() => setTaxType('')}
                  />
                )}
                {active && (
                  <Chip
                    label={`Status: ${active === 'true' ? 'Enabled' : 'Disabled'}`}
                    size="small"
                    onDelete={() => setActive('')}
                  />
                )}
                <Chip
                  color="primary"
                  label="Clear all"
                  size="small"
                  variant="outlined"
                  onClick={clearFilters}
                />
              </Stack>
            )}
          </Stack>
        </Card.Section>

        {loading ? (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </Stack>
          </Card.Section>
        ) : error ? (
          <Card.Section>
            <Body color="lighter">{error}</Body>
          </Card.Section>
        ) : workers.length === 0 ? (
          <Card.Section>
            <Stack vertical style={{ alignItems: 'center', padding: '40px' }}>
              <Body color="lighter">No workers found</Body>
              {hasActiveFilters && (
                <Small color="lighter">Try adjusting your filters</Small>
              )}
            </Stack>
          </Card.Section>
        ) : (
          <Table>
            <TableHeader fields={TABLE_HEADERS} />
            <tbody>
              {workers.map((worker) => (
                <tr key={worker.id}>
                  <td style={{ width: 80, padding: '12px 8px' }}>
                    <a
                      href={`/workers_v2/${worker.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#1976d2',
                        textDecoration: 'none',
                        fontWeight: 600,
                        display: 'block',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {worker.id}
                    </a>
                  </td>
                  <td>
                    <Stack vertical gap={2}>
                      <a
                        href={`/workers_v2/${worker.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#1976d2',
                          textDecoration: 'none',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Body weight="semibold">{worker.user.full_name}</Body>
                      </a>
                      {worker.user.phone_number && (
                        <Small color="lighter">
                          {worker.user.phone_number}
                        </Small>
                      )}
                    </Stack>
                  </td>
                  <td>
                    <Small>{worker.user.email}</Small>
                  </td>
                  <td>
                    <Badge
                      label={worker.tax_type.toUpperCase()}
                      status={worker.tax_type === 'w2' ? 'theme' : 'neutral'}
                    />
                  </td>
                  <td>
                    <Badge
                      label={formatStatus(worker.statuses.onboarding_status)}
                      status={
                        statusColors[worker.statuses.onboarding_status] ||
                        'neutral'
                      }
                    />
                  </td>
                  <td>
                    <Badge
                      label={formatStatus(worker.statuses.i9_status)}
                      status={
                        statusColors[worker.statuses.i9_status] || 'neutral'
                      }
                    />
                  </td>
                  <td>
                    <Badge
                      label={worker.active ? 'Enabled' : 'Disabled'}
                      status={worker.active ? 'theme' : 'danger'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {pagination && workers.length > 0 && (
        <Paginator
          currentPage={pagination.page}
          handleNumberItemsChange={handleNumberItemsChange}
          handlePageChange={handlePageChange}
          itemsLength={pagination.total}
          itemsPerPage={itemsPerPage}
        />
      )}
    </Page>
  );
};

export default WorkersV2;
